import { useEffect, useState } from 'react';

// Below this width the 240px expanded sidebar plus the canvas padding eats
// ~304px of chrome, so there is almost no canvas left and every dashboard tile
// overflows its box (measured at a 320px viewport: 38px-wide stat cards,
// 42-53px of overflow per tile). Collapsing the sidebar to 56px clears all of
// it. 699px reuses the breakpoint `.filter-row .filter-pair.two-up` already
// uses in components.css rather than inventing a new one, and sits far enough
// above the ~480px measured threshold to leave headroom for longer copy and
// larger default font sizes.
export const NARROW_VIEWPORT_QUERY = '(max-width: 699px)';

/**
 * True while the viewport is too narrow for the expanded sidebar.
 *
 * This is a DERIVED value and is deliberately never persisted. Callers OR it
 * with the user's stored collapse preference instead of writing to it, so a
 * trip through a narrow viewport does not silently rewrite what the user chose
 * — widen the window again and their own preference is back.
 */
export function useNarrowViewport(query = NARROW_VIEWPORT_QUERY) {
  // Seeded from matchMedia rather than from `false` so a narrow first paint is
  // already collapsed — otherwise the shell renders expanded for a frame and
  // visibly snaps shut.
  const [isNarrow, setIsNarrow] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const handleChange = (event) => setIsNarrow(event.matches);
    // Re-read on subscribe: the listener only fires on transitions after it is
    // attached, so a resize between the first render and this effect (or a
    // changed `query`) would otherwise be missed.
    setIsNarrow(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [query]);

  return isNarrow;
}

export default useNarrowViewport;
